import type { LandingContent } from '../content/landingContentTypes.js';

const preferredPaths = ['/bewerben', '/globus', '/team', '/ueber-vivaporta', '/kontakt'];
const isApplicationLink = (href: string) => {
  try {
    const url = new URL(href, 'https://www.vivaporta.org');
    return url.pathname.replace(/\/$/u, '') === '/basvuru-formu' &&
      (url.hostname.endsWith('.vivaporta.org') || url.hostname.includes('platform'));
  } catch { return false; }
};

/** One-time migration. Subsequent editor saves keep the chosen text, links and order. */
export const migrateFooterLinks = (content: LandingContent, defaults: LandingContent, revision = 17): Map<number, number> => {
  const links = Array.isArray(content.footer.links) ? content.footer.links : [];
  const indexed = links.map((link, index) => ({
    link: { ...link, href: revision < 18 && isApplicationLink(link.href) ? '/bewerben' : link.href }, index,
  }));
  const preferred = revision < 18 ? preferredPaths.map(href => {
    const existing = indexed.find(item => item.link.href === href);
    const fallback = defaults.footer.links.find(link => link.href === href)!;
    if (!existing) return { link: { ...fallback }, index: -1 };
    // Rename the old default only; retain custom editorial labels.
    if (href === '/ueber-vivaporta' && ['Über Viva Porta', 'About Viva Porta', 'Viva Porta hakkında'].includes(existing.link.label)) {
      existing.link.label = fallback.label;
    }
    return existing;
  }) : [];
  const seenApplications = new Set<string>();
  const ordered = [...preferred, ...indexed.filter(item => !preferred.includes(item))].filter(({ link }) => {
    let application = isApplicationLink(link.href);
    try {
      const url = new URL(link.href, 'https://www.vivaporta.org');
      application ||= ['www.vivaporta.org', 'vivaporta.org'].includes(url.hostname) && url.pathname.replace(/\/$/u, '') === '/bewerben';
    } catch { /* Preserve custom targets that are not URLs. */ }
    if (!application) return true;
    // Keep editorial labels, but collapse repeated application CTAs left by older saves.
    const label = link.label.trim().replace(/\s+/gu, ' ').toLocaleLowerCase(content.locale);
    if (seenApplications.has(label)) return false;
    seenApplications.add(label);
    return true;
  });
  content.footer.links = ordered.map(item => item.link);
  return new Map(ordered.flatMap((item, index) => item.index < 0 ? [] : [[item.index, index]]));
};

export const remapFooterKeys = <T>(entries: Record<string, T>, order: Map<number, number>): Record<string, T> =>
  Object.fromEntries(Object.entries(entries).filter(([key]) => {
    const match = key.match(/(?:^|[.:])footer\.link\.(\d+)(?=\.|$)/u);
    return !match || order.has(Number(match[1]));
  }).map(([key, value]) => [
    key.replace(/(^|[.:])footer\.link\.(\d+)(?=\.|$)/u, (match, prefix: string, oldIndex: string) => {
      const index = order.get(Number(oldIndex));
      return index === undefined ? match : `${prefix}footer.link.${index}`;
    }), value,
  ]));
